import { HttpClient, HttpEvent, HttpEventType, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EMPTY, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { HttpclienterrorService } from '../interceptor/httpclienterror.service';
import { FilesStructure } from '../models/uploads-class';

@Injectable({
  providedIn: 'root',
})
export class UploadService {
  private _inFlightDownloads = new Map<string, Observable<any>>();

  constructor(
    private http: HttpClient,
    public httpclienterrorService: HttpclienterrorService,
  ) {}

  GetFilesStructureBySource(Source: string, idProduct: number, idProductsTypes: number): Observable<FilesStructure[]> {
    return this.http
      .get<
        FilesStructure[]
      >(environment.URL + `/uploads/GetFilesStructureBySource/${Source}/${idProduct}/${idProductsTypes}?ngsw-bypass=true`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  private normalizeUploadEvent(event: HttpEvent<any>, fallbackName?: string): HttpEvent<any> {
    if (event.type === HttpEventType.Response && event.body) {
      const body = event.body as any;
      const resolvedName = body.originalName || fallbackName || body.name;
      const normalizedBody = {
        ...body,
        name: resolvedName,
        originalName: body.originalName || resolvedName,
      };
      return new HttpResponse({ ...event, body: normalizedBody });
    }
    return event;
  }

  upload(files: File, nif: string, path: string, customName?: string): Observable<HttpEvent<any>> {
    const formData = new FormData();
    const uploadName = customName || files?.name;
    // Verificação do arquivo
    if (files && files.size > 0) {
      // Usar nome customizado se fornecido
      formData.append('file', files, uploadName);
    } else {
      console.log('Arquivo vazio');
      return EMPTY;
    }

    return this.http
      .post(environment.URL + `/uploads/upload/${nif}/${path}?ngsw-bypass=true`, formData, {
        observe: 'events',
        reportProgress: true,
        responseType: 'json',
      })
      .pipe(map((event: HttpEvent<any>) => this.normalizeUploadEvent(event, uploadName)));
  }

  /** Lista ficheiros na pasta NIF/subpath */
  listFiles(nif: string, subpath: string): Observable<any[]> {
    return this.http.get<any[]>(environment.URL + `/uploads/listFiles/${nif}/${subpath}?ngsw-bypass=true`).pipe(
      catchError(error => {
        console.error('Erro ao listar ficheiros', error);
        return [];
      }),
    );
  }

  uploadProductFiles(files: File, idCompanie: number, idProductsTypes: number) {
    const formData = new FormData();
    formData.append('file', files, files.name);

    return this.http
      .post(
        environment.URL + `/uploads/uploadproductfiles/${idCompanie}/${idProductsTypes}?ngsw-bypass=true`,
        formData,
        {
          observe: 'events',
          reportProgress: true,
          responseType: 'json',
        },
      )
      .pipe(map((event: HttpEvent<any>) => this.normalizeUploadEvent(event, files?.name)));
  }

  uploadproductclaimsfiles(files: File, idCompanie: number, idProductsTypes: number) {
    const formData = new FormData();
    formData.append('file', files, files.name);

    return this.http
      .post(
        environment.URL + `/uploads/uploadproductclaimsfiles/${idCompanie}/${idProductsTypes}?ngsw-bypass=true`,
        formData,
        {
          observe: 'events',
          reportProgress: true,
          responseType: 'json',
        },
      )
      .pipe(map((event: HttpEvent<any>) => this.normalizeUploadEvent(event, files?.name)));
  }

  uploadProductsTypesPurchaseConfigPDF(files: File, idCompanie: number, idProductsTypesProposals: number) {
    const formData = new FormData();
    formData.append('file', files, files.name);

    return this.http
      .post(
        environment.URL +
          `/uploads/uploadProductsTypesPurchaseConfigPDF/${idCompanie}/${idProductsTypesProposals}?ngsw-bypass=true`,
        formData,
        {
          observe: 'events',
          reportProgress: true,
          responseType: 'json',
        },
      )
      .pipe(map((event: HttpEvent<any>) => this.normalizeUploadEvent(event, files?.name)));
  }

  uploadInsuranceClaimsConfigPDF(files: File, idCompanie: number, idInsuranceClaimsQuizConfigStepper: number) {
    const formData = new FormData();
    formData.append('file', files, files.name);

    return this.http
      .post(
        environment.URL +
          `/uploads/uploadInsuranceClaimsConfigPDF/${idCompanie}/${idInsuranceClaimsQuizConfigStepper}?ngsw-bypass=true`,
        formData,
        {
          observe: 'events',
          reportProgress: true,
          responseType: 'json',
        },
      )
      .pipe(map((event: HttpEvent<any>) => this.normalizeUploadEvent(event, files?.name)));
  }

  download(path: string, options?: { skipError?: boolean }): Observable<any> {
    try {
      if (!path || path.trim() === '') {
        return of(null);
      }

      // Normalizar barras e remover barra inicial para evitar // na URL
      const cleanPath = path.replace(/\\/g, '/').replace(/^\/+/, '');
      if (this._inFlightDownloads.has(cleanPath)) {
        return this._inFlightDownloads.get(cleanPath)!;
      }

      const encodedPath = cleanPath
        .split('/')
        .map(segment => encodeURIComponent(segment))
        .join('/');

      let headers = new HttpHeaders();
      const isPhoto = cleanPath.includes('/photo/') || cleanPath.includes('/avatar/');
      if (options?.skipError || isPhoto) {
        headers = headers.set('X-Skip-Error', 'true');
      }

      const req$ = this.http
        .get(environment.URL + `/uploads/download/${encodedPath}?ngsw-bypass=true`, {
          headers,
          responseType: 'blob' as 'json',
        })
        .pipe(
          catchError(error => {
            this._inFlightDownloads.delete(cleanPath);
            if (options?.skipError || isPhoto) {
              return of(null);
            }
            console.log(error);
            return throwError(() => error);
          }),
          finalize(() => {
            if (isPhoto) {
              setTimeout(() => this._inFlightDownloads.delete(cleanPath), 2000);
            } else {
              this._inFlightDownloads.delete(cleanPath);
            }
          }),
          shareReplay(1),
        );

      this._inFlightDownloads.set(cleanPath, req$);
      return req$;
    } catch (error) {
      console.log(error);
      return of(null);
    }
  }

  /**
   * Download público de documentos de produto (sem autenticação).
   * Usar exclusivamente para ficheiros em pastas públicas: ProductsDocs,
   * ProductsPurchaseConfigPDF, InsuranceClaimsConfigPDF.
   * Funciona em mobile e em utilizadores não autenticados.
   */
  publicDownload(path: string): Observable<any> {
    try {
      if (!path || path.trim() === '') return EMPTY;

      const cleanPath = path.replace(/\\/g, '/').replace(/^\/+/, '');
      const encodedPath = cleanPath
        .split('/')
        .map(segment => encodeURIComponent(segment))
        .join('/');

      return this.http
        .get(environment.URL + `/uploads/public-download/${encodedPath}?ngsw-bypass=true`, {
          responseType: 'blob' as 'json',
        })
        .pipe(
          catchError(error => {
            console.log('[publicDownload] Erro:', error);
            return throwError(() => error);
          }),
        );
    } catch (error) {
      console.log('[publicDownload] Exceção:', error);
      return EMPTY;
    }
  }

  handleFilebk(res: any, fileName: string) {
    const file = new Blob([res], {
      type: res.type,
    });

    // // para funcionar no IE
    // if (window.navigator && window.navigator.msSaveOrOpenBlob) {
    //   window.navigator.msSaveOrOpenBlob(file);
    //   return;
    // }

    const blob = window.URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = blob;
    link.download = fileName;

    // Esta opção deixou de ser necessária browsers mais recentes
    // link.click();

    // por causa do firefox em vez de link.click();
    link.dispatchEvent(
      new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window,
      }),
    );

    setTimeout(() => {
      // firefox
      window.URL.revokeObjectURL(blob);
      link.remove();
    }, 100);
  }

  handleFile(res: any, fileName: string): Promise<void> {
    return new Promise<void>(resolve => {
      const file = new Blob([res], { type: res.type });
      const blob = window.URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = blob;
      link.download = fileName;

      link.dispatchEvent(
        new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: window,
        }),
      );

      setTimeout(() => {
        window.URL.revokeObjectURL(blob);
        link.remove();
        resolve();
      }, 100);
    });
  }

  /** Elimina um ficheiro do disco no servidor */
  deleteFile(filePath: string): Observable<any> {
    return this.http
      .delete(environment.URL + `/uploads/deleteFile?ngsw-bypass=true`, {
        body: { filePath },
      })
      .pipe(
        catchError(error => {
          console.error('Erro ao eliminar ficheiro', error);
          return throwError(() => error);
        }),
      );
  }
}
